import { MAX_UPLOAD_FILES_PER_SESSION } from '@/utils/uploadSessionLimits';
import { NAV_HUB_CARD_IMAGES } from './navHubCardImages';

export const AI_OUTPAINT_MAX_SOURCES = MAX_UPLOAD_FILES_PER_SESSION;

// 经 workbench 兼容 shim 再导出，避免 deep import；禁止走 @/image-crop barrel（会与本文件循环）
export {
  IMAGE_CROP_MAX_SOURCES,
  IMAGE_CROP_QWEN_BATCH_SIZE,
  IMAGE_CROP_NAV_ID,
  IMAGE_CROP_POLL_INTERVAL_MS,
  getImageCropStudioConfig,
} from './imageCropConfig';

export const GOODS_STUDIO_MAX_STAMPS = MAX_UPLOAD_FILES_PER_SESSION;

export function getAiOutpaintStudioConfig() {
  const images = NAV_HUB_CARD_IMAGES.aiOutpaint;
  return {
    title: 'AI扩图',
    subtitle: '智能扩展画布，补全背景与构图',
    hero: {
      image: images?.result,
      caption: '保持主体不变，向外扩展画面',
    },
    tryExamples: [
      { id: 'demo-1', thumb: images?.source, sampleUrl: images?.source },
      { id: 'demo-2', thumb: images?.result, sampleUrl: images?.result },
    ],
  };
}

export function getGoodsStudioConfig() {
  const images = NAV_HUB_CARD_IMAGES.goods;
  return {
    title: '主图创作',
    subtitle: '批量创作主图，一键上架',
    hero: {
      image: images?.result || images?.source,
      caption: '上传印花图，AI 一键生成主图与详情页',
    },
    tryExamples: [
      { id: 'demo-1', thumb: images?.source, sampleUrl: images?.source },
      { id: 'demo-2', thumb: images?.result, sampleUrl: images?.source },
    ],
  };
}

export const GOODS_STUDIO_PRODUCT_INFO_MAX = 500;

export const GOODS_STUDIO_COUNT_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({
  value: n,
  label: String(n),
}));

/** 主图创作 · 默认市场风格编码 */
export const GOODS_STUDIO_DEFAULT_COUNTRY_STYLE = 'classic';

/**
 * 主图创作 · 目标国家（各国含多种可选市场风格，文案 ≤60 字）
 * styles[].value 在各国间复用：classic / lifestyle / premium
 */
export const GOODS_STUDIO_COUNTRY_OPTIONS = [
  {
    value: 'cn',
    label: '中国',
    styles: [
      {
        value: 'classic',
        label: '白底卖点',
        copy: '面向国内电商：白底清晰、卖点直观，构图干净实用，适合淘宝天猫主图习惯。',
      },
      {
        value: 'lifestyle',
        label: '场景种草',
        copy: '面向国内电商：居家/日常场景自然光，氛围真实有代入感，突出使用体验。',
      },
      {
        value: 'premium',
        label: '质感细节',
        copy: '面向国内电商：近景突出面料与做工，光影干净，呈现品质感与细节可信度。',
      },
    ],
  },
  {
    value: 'us',
    label: '美国',
    styles: [
      {
        value: 'classic',
        label: '简约专业',
        copy: '面向美国站：构图简约大气、留白充足，突出商品本身，风格干净专业易转化。',
      },
      {
        value: 'lifestyle',
        label: '生活场景',
        copy: '面向美国站：轻松生活化场景，自然光与真实氛围，突出日常使用与舒适感。',
      },
      {
        value: 'premium',
        label: '高端质感',
        copy: '面向美国站：高级静物质感，柔光细腻，色调克制，强调品牌感与品质细节。',
      },
    ],
  },
  {
    value: 'uk',
    label: '英国',
    styles: [
      {
        value: 'classic',
        label: '克制高级',
        copy: '面向英国站：克制高级、色调柔和，避免夸张道具，呈现品质感与精致静物风。',
      },
      {
        value: 'lifestyle',
        label: '英伦日常',
        copy: '面向英国站：柔和自然光与轻场景，气质内敛，突出日常穿搭与生活质感。',
      },
      {
        value: 'premium',
        label: '精致静物',
        copy: '面向英国站：精致静物构图，低饱和色调，细节清晰，呈现典雅与品质感。',
      },
    ],
  },
  {
    value: 'de',
    label: '德国',
    styles: [
      {
        value: 'classic',
        label: '严谨质感',
        copy: '面向德国站：强调质感与设计感，构图严谨利落，光线均匀，突出做工细节。',
      },
      {
        value: 'lifestyle',
        label: '实用场景',
        copy: '面向德国站：简洁实用场景，光线真实，突出耐用、功能与日常可信度。',
      },
      {
        value: 'premium',
        label: '工业设计',
        copy: '面向德国站：工业设计感构图，结构清晰、光影干净，突出精密与材质质感。',
      },
    ],
  },
  {
    value: 'fr',
    label: '法国',
    styles: [
      {
        value: 'classic',
        label: '优雅精致',
        copy: '面向法国站：优雅精致、氛围轻柔，画面高级感强，突出版型与审美气质。',
      },
      {
        value: 'lifestyle',
        label: '法式氛围',
        copy: '面向法国站：柔和氛围光与轻场景，浪漫克制，突出审美气质与日常优雅。',
      },
      {
        value: 'premium',
        label: '高级时装',
        copy: '面向法国站：高级时装感构图，留白考究，色调柔和，突出版型与材质高级感。',
      },
    ],
  },
  {
    value: 'jp',
    label: '日本',
    styles: [
      {
        value: 'classic',
        label: '日系简约',
        copy: '面向日本站：精致克制、留白干净，细节清晰，呈现简约高级的日系电商感。',
      },
      {
        value: 'lifestyle',
        label: '清爽生活',
        copy: '面向日本站：清爽生活场景，柔和自然光，干净温馨，突出日常好感与精致感。',
      },
      {
        value: 'premium',
        label: '极简留白',
        copy: '面向日本站：极简留白构图，细节清晰、色调克制，呈现高级与安静的视觉气质。',
      },
    ],
  },
  {
    value: 'kr',
    label: '韩国',
    styles: [
      {
        value: 'classic',
        label: '潮流利落',
        copy: '面向韩国站：潮流利落、色彩鲜明，构图时尚有活力，突出流行穿搭气质。',
      },
      {
        value: 'lifestyle',
        label: '街拍氛围',
        copy: '面向韩国站：轻街拍/日常氛围，色彩鲜明，构图有活力，突出流行感与穿搭气质。',
      },
      {
        value: 'premium',
        label: '时尚大片',
        copy: '面向韩国站：时尚大片感构图，光影利落，色彩干净，突出潮流高级与造型张力。',
      },
    ],
  },
  {
    value: 'au',
    label: '澳大利亚',
    styles: [
      {
        value: 'classic',
        label: '清爽自然',
        copy: '面向澳大利亚站：清爽自然、光线明亮，风格轻松，突出舒适实用与户外感。',
      },
      {
        value: 'lifestyle',
        label: '户外轻松',
        copy: '面向澳大利亚站：明亮户外/休闲场景，光线通透，突出舒适实用与轻松生活方式。',
      },
      {
        value: 'premium',
        label: '阳光质感',
        copy: '面向澳大利亚站：阳光通透的高级静物感，色调清爽，突出材质与舒适品质。',
      },
    ],
  },
  {
    value: 'ca',
    label: '加拿大',
    styles: [
      {
        value: 'classic',
        label: '干净实用',
        copy: '面向加拿大站：干净实用、色调中性，构图稳重清晰，突出耐用与日常品质。',
      },
      {
        value: 'lifestyle',
        label: '日常舒适',
        copy: '面向加拿大站：日常舒适场景，自然中性色调，突出耐用、温暖与生活实用感。',
      },
      {
        value: 'premium',
        label: '稳重品质',
        copy: '面向加拿大站：稳重清晰构图，中性色调与均匀光，突出耐用品质与材质可信度。',
      },
    ],
  },
  {
    value: 'sg',
    label: '新加坡',
    styles: [
      {
        value: 'classic',
        label: '清爽现代',
        copy: '面向新加坡站：清爽明快、国际感强，构图简洁现代，适合多元跨境审美。',
      },
      {
        value: 'lifestyle',
        label: '都市轻场景',
        copy: '面向新加坡站：都市轻场景与明亮光线，现代清爽，突出国际感与日常活力。',
      },
      {
        value: 'premium',
        label: '国际精致',
        copy: '面向新加坡站：国际精致构图，简洁现代、光影干净，突出多元审美与品质感。',
      },
    ],
  },
];

export function getGoodsStudioCountryOption(country) {
  return GOODS_STUDIO_COUNTRY_OPTIONS.find((item) => item.value === country) || null;
}

export function getGoodsStudioCountryStyles(country) {
  return getGoodsStudioCountryOption(country)?.styles || [];
}

export function getDefaultGoodsStudioCountryStyle(country) {
  return getGoodsStudioCountryStyles(country)[0]?.value || GOODS_STUDIO_DEFAULT_COUNTRY_STYLE;
}

/** 校验/回退国家风格；国家切换后若当前风格不存在则回退到该国默认风格 */
export function normalizeGoodsStudioCountryStyle(country, countryStyle) {
  const styles = getGoodsStudioCountryStyles(country);
  if (!styles.length) return GOODS_STUDIO_DEFAULT_COUNTRY_STYLE;
  if (styles.some((item) => item.value === countryStyle)) return countryStyle;
  return styles[0].value;
}

export function getGoodsStudioCountryStyleOption(country, countryStyle) {
  const styles = getGoodsStudioCountryStyles(country);
  const normalized = normalizeGoodsStudioCountryStyle(country, countryStyle);
  return styles.find((item) => item.value === normalized) || styles[0] || null;
}

export function getGoodsStudioCountryStyleLabel(country, countryStyle) {
  return getGoodsStudioCountryStyleOption(country, countryStyle)?.label || '';
}

export function getGoodsStudioCountryCopy(country, countryStyle) {
  return getGoodsStudioCountryStyleOption(country, countryStyle)?.copy || '';
}
export const GOODS_STUDIO_LANGUAGE_OPTIONS = [
  { value: 'en', label: '英文' },
  { value: 'zh', label: '中文' },
  { value: 'de', label: '德文' },
  { value: 'fr', label: '法文' },
  { value: 'ja', label: '日文' },
];

export const GOODS_STUDIO_MAIN_IMAGE_RATIO_OPTIONS = [
  { value: '1:1', label: '1:1' },
  { value: '3:4', label: '3:4' },
  { value: '4:3', label: '4:3' },
  { value: '2:3', label: '2:3' },
  { value: '3:2', label: '3:2' },
];

export const GOODS_STUDIO_DETAIL_PAGE_RATIO_OPTIONS = [
  { value: '3:4', label: '3:4' },
  { value: '1:1', label: '1:1' },
  { value: '4:3', label: '4:3' },
  { value: '2:3', label: '2:3' },
  { value: '9:16', label: '9:16' },
];

/** 主图创作 · 服装类目（非 SHEIN 刊登类目） */
export const GOODS_STUDIO_CATEGORY_OPTIONS = [
  { value: 'mens', label: '男装', modelType: 'male' },
  { value: 'womens', label: '女装', modelType: 'female' },
  { value: 'boys', label: '男童', modelType: 'boy' },
  { value: 'girls', label: '女童', modelType: 'girl' },
];

/** 主图创作 · 模特模式（仅 AI 生成与自定义上传） */
export const GOODS_STUDIO_MODEL_MODE_OPTIONS = [
  { value: 'ai', label: 'AI生成' },
  { value: 'custom', label: '自定义上传' },
];

/** 主图创作 · 背景风格（用户可选，避免每次固定同一背景） */
export const GOODS_STUDIO_BACKGROUND_STYLE_OPTIONS = [
  { value: 'urbanStreet', label: '都市街拍' },
  { value: 'cafe', label: '咖啡馆' },
  { value: 'campus', label: '校园青春' },
  { value: 'beach', label: '海边度假' },
  { value: 'skatePark', label: '滑板运动' },
  { value: 'nature', label: '自然风光' },
  { value: 'indoor', label: '室内家居' },
  { value: 'studio', label: '创意棚拍' },
  { value: 'nightCity', label: '霓虹夜景' },
  { value: 'garden', label: '花园庭院' },
];

/** 主图创作 · 取景构图 */
export const GOODS_STUDIO_COMPOSITION_OPTIONS = [
  { value: 'upperBody', label: '上身' },
  { value: 'lowerBody', label: '下身' },
  { value: 'fullBody', label: '全身' },
];

export const GOODS_STUDIO_DEFAULT_CATEGORY = 'womens';
export const GOODS_STUDIO_DEFAULT_MODEL_MODE = 'ai';
export const GOODS_STUDIO_DEFAULT_COMPOSITION = 'fullBody';
export const GOODS_STUDIO_DEFAULT_BACKGROUND_STYLE = 'urbanStreet';

export function resolveGoodsStudioModelType(goodsCategory) {
  const hit = GOODS_STUDIO_CATEGORY_OPTIONS.find((item) => item.value === goodsCategory);
  return hit?.modelType || 'female';
}

export function normalizeGoodsStudioParams(params = {}) {
  const goodsCategory = GOODS_STUDIO_CATEGORY_OPTIONS.some((item) => item.value === params.goodsCategory)
    ? params.goodsCategory
    : GOODS_STUDIO_DEFAULT_CATEGORY;
  const rawModelMode = String(params.modelMode || '').trim();
  const modelMode = GOODS_STUDIO_MODEL_MODE_OPTIONS.some((item) => item.value === rawModelMode)
    ? rawModelMode
    : (rawModelMode === 'system' || rawModelMode === 'none' ? 'ai' : GOODS_STUDIO_DEFAULT_MODEL_MODE);
  const composition = GOODS_STUDIO_COMPOSITION_OPTIONS.some((item) => item.value === params.composition)
    ? params.composition
    : GOODS_STUDIO_DEFAULT_COMPOSITION;
  const backgroundStyle = GOODS_STUDIO_BACKGROUND_STYLE_OPTIONS.some((item) => item.value === params.backgroundStyle)
    ? params.backgroundStyle
    : GOODS_STUDIO_DEFAULT_BACKGROUND_STYLE;
  return {
    ...params,
    goodsCategory,
    modelMode,
    composition,
    backgroundStyle,
    modelType: resolveGoodsStudioModelType(goodsCategory),
  };
}

export const GOODS_STUDIO_DEFAULT_PARAMS = {
  composeMode: 'ai',
  country: 'us',
  countryStyle: GOODS_STUDIO_DEFAULT_COUNTRY_STYLE,
  language: 'en',
  mainImageCount: 1,
  detailPageCount: 0,
  mainImageRatio: '1:1',
  detailPageRatio: '3:4',
  productInfo: '',
  goodsStudioPromptCaseId: '',
  goodsStudioPromptCaseScope: '',
  goodsCategory: GOODS_STUDIO_DEFAULT_CATEGORY,
  modelMode: GOODS_STUDIO_DEFAULT_MODEL_MODE,
  backgroundStyle: GOODS_STUDIO_DEFAULT_BACKGROUND_STYLE,
  composition: GOODS_STUDIO_DEFAULT_COMPOSITION,
  modelType: resolveGoodsStudioModelType(GOODS_STUDIO_DEFAULT_CATEGORY),
  modelEthnicity: 'asian',
  selectedPresetId: '',
  selectedPresetUrl: '',
  modelImageUrl: '',
  modelImagePreviewUrl: '',
  usedUploadedModel: false,
};

export const AI_TOOL_STUDIO_NAV_IDS = [
  'aiTextToImage',
  'aiCopywriting',
];

export const AI_TOOL_STUDIO_MAX_SOURCES = MAX_UPLOAD_FILES_PER_SESSION;
/** AI 文案单次上传上限（默认 / 自定义合计） */
export const AI_COPYWRITING_MAX_SOURCES = MAX_UPLOAD_FILES_PER_SESSION;
export const AI_TOOL_STUDIO_PROMPT_MAX_LENGTH = 500;
export const AI_TEXT2IMG_PROMPT_MAX_LENGTH = 1000;
export const AI_TOOL_STUDIO_GENERATE_COUNT_OPTIONS = [1, 2, 3, 4];

export const AI_TEXT2IMG_STYLE_OPTIONS = [
  { value: 'common', label: '通用' },
  { value: 'photo', label: '写实' },
  { value: 'eCommerce', label: '电商' },
  { value: 'illustration', label: '插画' },
  { value: 'pictureBook', label: '绘本' },
  { value: 'anime', label: '动漫' },
  { value: 'game', label: '游戏' },
  { value: '3D', label: '3D' },
  { value: 'logo', label: 'Logo' },
];

export const AI_TEXT2IMG_AR_OPTIONS = ['1:1', '2:3', '3:2', '3:4', '4:3', '9:16', '16:9'];

export function isAiToolStudioNav(navId) {
  return AI_TOOL_STUDIO_NAV_IDS.includes(navId);
}

const AI_TOOL_STUDIO_CONFIGS = {
  aiTextToImage: {
    title: '文生图',
    subtitle: '输入文字描述，AI生成高质量图片',
    uploadMode: 'none',
    generateLabel: '立即生成',
    legalNote: '根据文字提示词生成高质量图片，支持多种风格与场景',
    promptLabel: '画面描述',
    promptPlaceholder: '例如：一只可爱的猫咪坐在窗台上，阳光洒落，写实摄影风格…',
    promptMaxLength: AI_TEXT2IMG_PROMPT_MAX_LENGTH,
    promptSamples: [
      { label: '写实摄影', prompt: '白底商品摄影，柔和自然光，高清细节，电商主图风格' },
      { label: '插画风格', prompt: '扁平插画风格，明亮配色，简约背景，适合社交媒体' },
      { label: '场景氛围', prompt: '现代简约客厅，木质家具，暖色调，生活感氛围' },
    ],
    historyEmptyDescription: '文生图生成后，记录将自动保存在这里',
  },
  aiCopywriting: {
    title: 'AI文案',
    subtitle: '上传商品图，可选标题模板覆盖风格要求，一键生成卖点文案',
    uploadMode: 'required',
    uploadLabel: '商品图',
    generateLabel: '立即生成',
    legalNote: '',
    promptLabel: '文案风格',
    promptPlaceholder: '例如：奢华质感风、功效痛点风、种草口播风…',
    promptSamples: [
      { label: '种草风格', prompt: '口语化种草风格，突出使用场景与情绪共鸣' },
      { label: '专业卖点', prompt: '强调功能参数、材质工艺与差异化卖点' },
      { label: '促销爆款', prompt: '突出限时优惠与紧迫感，适合大促主会场' },
    ],
    historyEmptyDescription: '文案生成后，记录将自动保存在这里',
  },
};

export function getAiToolStudioConfig(navId) {
  const base = AI_TOOL_STUDIO_CONFIGS[navId];
  if (!base) return null;
  const images = NAV_HUB_CARD_IMAGES[navId] || {};
  const heroImage = images.result || images.source || NAV_HUB_CARD_IMAGES.img2img?.result;
  const sourceImage = images.source || heroImage;
  return {
    navId,
    ...base,
    hero: {
      image: heroImage,
      before: images.source,
      caption: base.legalNote,
    },
    tryExamples: [
      { id: 'demo-1', thumb: sourceImage, sampleUrl: sourceImage, prompt: base.promptSamples?.[0]?.prompt || '' },
      { id: 'demo-2', thumb: heroImage, sampleUrl: sourceImage, prompt: base.promptSamples?.[1]?.prompt || '' },
    ],
    promptMaxLength: base.promptMaxLength || AI_TOOL_STUDIO_PROMPT_MAX_LENGTH,
    maxSources: navId === 'aiCopywriting' ? AI_COPYWRITING_MAX_SOURCES : AI_TOOL_STUDIO_MAX_SOURCES,
    generateCountOptions: AI_TOOL_STUDIO_GENERATE_COUNT_OPTIONS,
  };
}
